/**
 * FeaturedLessonDelivery.jsx — Feb 2026 mid-month.
 *
 * Public page rendered at `/featured-lesson/:token`. The token itself
 * is the auth: any teacher with the link (from their email) can
 * download the PDF + slide deck for 7 days. No login required.
 *
 * Includes a CASL-clean opt-in toggle (checkbox DEFAULTS to OFF per
 * founder decision Feb 2026) that subscribes the teacher to the
 * Atomic Edu mailing list via EmailOctopus. The download works either
 * way — subscription is genuinely optional.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, FileText, Presentation, Mail, Sparkles } from "lucide-react";
import {
  fetchDeliveryPage,
  toggleDeliveryOptIn,
  deliveryPdfUrl,
  deliverySlidesUrl,
} from "../lib/featuredLessonApi";

const FeaturedLessonDelivery = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [subscribed, setSubscribed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDeliveryPage(token)
      .then((d) => {
        if (cancelled) return;
        setState({ loading: false, error: null, data: d });
        setSubscribed(!!d.subscribed);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail ||
          "This link is no longer available.";
        setState({ loading: false, error: detail, data: null });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onToggle = async (next) => {
    setSubscribed(next); // optimistic
    setSaving(true);
    try {
      await toggleDeliveryOptIn(token, next);
      toast.success(
        next
          ? "You're on the list — we'll email when new free lessons drop."
          : "You've been unsubscribed. The download still works.",
      );
    } catch (err) {
      setSubscribed(!next); // revert
      toast.error("Could not update your preference. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) {
    return (
      <div
        data-testid="featured-delivery-loading"
        className="min-h-screen flex items-center justify-center bg-[#f4f5f7] text-[#4a6984] text-sm font-semibold uppercase tracking-[0.14em]"
      >
        Loading your lesson…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center px-4">
        <div
          data-testid="featured-delivery-error"
          className="max-w-md w-full bg-white rounded-2xl border border-[#4a6984]/15 p-8 text-center shadow-sm"
        >
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Featured Lesson
          </p>
          <h1 className="mt-3 font-black text-2xl text-[#1c2d3d]">
            Link unavailable
          </h1>
          <p className="mt-3 text-sm text-[#4a6984] leading-relaxed">
            {state.error}
          </p>
          <Link
            to="/"
            data-testid="featured-delivery-error-home"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            Return to Atomic Edu
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;
  const meta = [d.grade, d.subject].filter(Boolean).join(" · ");
  const pdfHref = deliveryPdfUrl(token);
  const slidesHref = deliverySlidesUrl(token);
  const expiresAt = d.expires_at ? new Date(d.expires_at) : null;
  const expiresLabel = expiresAt
    ? expiresAt.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "in 7 days";

  return (
    <div className="min-h-screen bg-[#f4f5f7] py-12 sm:py-16 px-4 sm:px-6">
      <div
        data-testid="featured-delivery-page"
        className="max-w-2xl mx-auto"
      >
        <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
          <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          Atomic Edu · Featured Lesson
        </p>
        <h1
          data-testid="featured-delivery-title"
          className="mt-3 font-black text-3xl sm:text-4xl tracking-tight text-[#1c2d3d]"
        >
          {d.activity_name}
        </h1>
        {meta ? (
          <p
            data-testid="featured-delivery-meta"
            className="mt-2 text-sm font-extrabold uppercase tracking-[0.14em] text-[#4a6984]"
          >
            {meta}
          </p>
        ) : null}
        {d.tagline ? (
          <p className="mt-4 text-base sm:text-lg text-[#1c2d3d] italic leading-relaxed">
            {d.tagline}
          </p>
        ) : null}
        {d.overview ? (
          <p className="mt-4 text-sm sm:text-base text-[#4a6984] leading-relaxed">
            {d.overview}
          </p>
        ) : null}

        {/* Download cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <a
            href={pdfHref}
            data-testid="featured-delivery-pdf-btn"
            className="group rounded-2xl border border-[#4a6984]/15 bg-white p-6 shadow-sm hover:shadow-md hover:border-[#96ded1] transition-all"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#96ded1]/20 text-[#1c2d3d]">
              <FileText className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <h2 className="mt-4 font-black text-lg text-[#1c2d3d]">
              Lesson package (PDF)
            </h2>
            <p className="mt-1 text-sm text-[#4a6984] leading-relaxed">
              Full lesson plan, student sheets, exit ticket — print-ready.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] group-hover:underline">
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </span>
          </a>
          <a
            href={slidesHref}
            data-testid="featured-delivery-slides-btn"
            className="group rounded-2xl border border-[#4a6984]/15 bg-white p-6 shadow-sm hover:shadow-md hover:border-[#96ded1] transition-all"
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#e6a63a]/20 text-[#1c2d3d]">
              <Presentation className="h-5 w-5" strokeWidth={2.2} />
            </span>
            <h2 className="mt-4 font-black text-lg text-[#1c2d3d]">
              Student slide deck (PPTX)
            </h2>
            <p className="mt-1 text-sm text-[#4a6984] leading-relaxed">
              Projectable deck aligned to the lesson. Opens in PowerPoint or Slides.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] group-hover:underline">
              <Download className="h-3.5 w-3.5" />
              Download slides
            </span>
          </a>
        </div>

        {/* Opt-in toggle */}
        <div className="mt-8 rounded-2xl border border-[#4a6984]/15 bg-white p-5 sm:p-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="featured-delivery-optin-toggle"
              checked={subscribed}
              disabled={saving}
              onChange={(e) => onToggle(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-[#4a6984]/30 text-[#1c2d3d] focus:ring-[#96ded1]"
            />
            <span className="flex-1">
              <span className="block text-sm font-extrabold text-[#1c2d3d]">
                Email me when new free lessons drop
              </span>
              <span className="block mt-1 text-xs text-[#4a6984] leading-relaxed">
                We send a fresh Ontario-aligned lesson every so often. No spam, no strings — the download above works either way. You can unsubscribe from this page or any email footer.
              </span>
            </span>
          </label>
        </div>

        {/* Meta strip */}
        <p
          data-testid="featured-delivery-expiry"
          className="mt-6 text-xs text-[#4a6984]/70 font-semibold"
        >
          <Mail className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
          Sent to <span className="font-extrabold">{d.email}</span>. This link is good until {expiresLabel}.
        </p>

        <div className="mt-10 pt-6 border-t border-[#4a6984]/10">
          <Link
            to="/"
            data-testid="featured-delivery-home-link"
            className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d]"
          >
            ← Return to Atomic Edu
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeaturedLessonDelivery;
